import React, { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import Page404 from "app/not-found";
import axios from "axios";
import { Buffer } from "buffer";

const API_URL = process.env.REACT_APP_API_URL;

// Format YYYY-MM-DD
const formatDate = (date) => {
  if (!date) return "";
  return new Date(date).toISOString().split("T")[0];
};

// ==================== Utilitaire preview retina ====================
const getRetinaAvatarPreview = (imgData, contentType, size = 32) => {
  return new Promise((resolve) => {
    const img = new Image();
    img.src = `data:${contentType};base64,${Buffer.from(imgData).toString(
      "base64"
    )}`;

    img.onload = () => {
      const scale = window.devicePixelRatio || 1;
      const canvasSize = size * scale;

      const canvas = document.createElement("canvas");
      canvas.width = canvasSize;
      canvas.height = canvasSize;

      const ctx = canvas.getContext("2d");
      ctx.clearRect(0, 0, canvasSize, canvasSize);
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = "high";

      // Ratio pour ne pas déformer
      const ratio = Math.max(canvasSize / img.width, canvasSize / img.height);
      const newWidth = img.width * ratio;
      const newHeight = img.height * ratio;
      const dx = (canvasSize - newWidth) / 2;
      const dy = (canvasSize - newHeight) / 2;

      ctx.drawImage(img, dx, dy, newWidth, newHeight);
      resolve(canvas.toDataURL("image/png"));
    };

    img.onerror = () => resolve(null);
  });
};

const PageProjet = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.user);

  const [projets, setProjets] = useState([]);
  const [projet, setProjet] = useState(null);
  const [votes, setVotes] = useState([]);
  const [votePreviews, setVotePreviews] = useState({}); // { voteId: previewURL }
  const [auteurPreview, setAuteurPreview] = useState(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [hoverVote, setHoverVote] = useState(false);
  const [voteLoading, setVoteLoading] = useState(false);
  const [hasVoted, setHasVoted] = useState(false);

  /* ================= FETCH PROJET + VOTES ================= */
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [projetsRes, projetRes, votesRes] = await Promise.all([
          axios.get(`${API_URL}/api/projets`),
          axios.get(`${API_URL}/api/projets/${id}`),
          axios.get(`${API_URL}/api/votes/${id}`),
        ]);

        const projetsTries = projetsRes.data.sort(
          (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
        );

        setProjets(projetsTries);
        setProjet(projetRes.data);
        setVotes(votesRes.data);

        // Générer previews pour votes
        const previews = {};
        await Promise.all(
          votesRes.data.map(async (vote) => {
            if (vote.user?.img) {
              const preview = await getRetinaAvatarPreview(
                vote.user.img.data,
                vote.user.img.contentType,
                32
              );
              previews[vote._id] = preview;
            }
          })
        );
        setVotePreviews(previews);

        // Générer preview auteur
        if (projetRes.data.auteur?.img) {
          const auteurPrev = await getRetinaAvatarPreview(
            projetRes.data.auteur.img.data,
            projetRes.data.auteur.img.contentType,
            32
          );
          setAuteurPreview(auteurPrev);
        }
      } catch (err) {
        console.error(err);
        setError("Impossible de charger le projet");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  /* ================= CHECK SI L’USAGER A VOTÉ ================= */
  useEffect(() => {
    if (!user) {
      setHasVoted(false);
      return;
    }
    const voted = votes.some((v) => v.user?._id === user._id);
    setHasVoted(voted);
  }, [votes, user]);

  /* ================= NAVIGATION ================= */
  const indexCourant = useMemo(
    () => projets.findIndex((p) => p._id === id),
    [projets, id]
  );

  const projetPrecedent =
    indexCourant > 0 ? projets[indexCourant - 1] : null;

  const projetSuivant =
    indexCourant >= 0 && indexCourant < projets.length - 1
      ? projets[indexCourant + 1]
      : null;

  /* ================= HANDLE VOTE ================= */
  const handleVote = async () => {
    if (!user) {
      alert("Vous devez être connecté pour voter.");
      return;
    }

    try {
      setVoteLoading(true);
      if (hasVoted) {
        await axios.delete(`${API_URL}/api/votes/${projet._id}/${user._id}`);
      } else {
        await axios.post(`${API_URL}/api/votes`, {
          userId: user._id,
          projetId: projet._id,
        });
      }

      // Rafraîchir votes
      const votesRes = await axios.get(`${API_URL}/api/votes/${projet._id}`);
      setVotes(votesRes.data);

      // Régénérer previews votes
      const previews = {};
      await Promise.all(
        votesRes.data.map(async (vote) => {
          if (vote.user?.img) {
            const preview = await getRetinaAvatarPreview(
              vote.user.img.data,
              vote.user.img.contentType,
              32
            );
            previews[vote._id] = preview;
          }
        })
      );
      setVotePreviews(previews);
    } catch (err) {
      console.error(err);
      alert("Erreur lors du vote.");
    } finally {
      setVoteLoading(false);
    }
  };

  if (loading) return <p className="text-center mt-10">Chargement du projet…</p>;
  if (error || !projet)
    return (
      <p className="text-center mt-10 text-red-600">
        {error || "Projet introuvable"}
      </p>
    );

  if (!user) return <Page404 />;

  return (
    <div className="max-w-6xl mx-auto p-4">
      <button
        onClick={() => navigate("/admin/listeProjets")}
        className="mb-6 text-blue-600 hover:underline"
      >
        ← Retour aux projets
      </button>

      <div className="flex items-start gap-4">
        {/* Flèche gauche */}
        {projetPrecedent && (
          <button
            onClick={() => navigate(`/admin/projets/${projetPrecedent._id}`)}
            className="mt-[25%] w-14 h-14 rounded-full bg-blue-600 text-white text-4xl"
          >
            ‹
          </button>
        )}

        {/* Carte projet */}
        <div className="flex-1">
          <div className="bg-white rounded-2xl shadow-lg p-6 relative">

            {/* ================= VOTES ================= */}
            <div className="absolute top-4 right-4 flex flex-col items-end gap-2">

              {/* Ligne 1 : Qte + avatars */}
              <div className="flex items-center gap-2">
                <span className="font-semibold text-sm">Votes : {votes.length}</span>
                <div className="flex -space-x-2">
                  {votes.map(
                    (vote) =>
                      votePreviews[vote._id] && (
                        <div key={vote._id} className="relative group">
                          <img
                            src={votePreviews[vote._id]}
                            alt={vote.user?.name}
                            className="w-8 h-8 rounded-full border-2 border-white object-cover"
                          />
                          <div
                            className="absolute top-10 left-1/2 -translate-x-1/2
                                       bg-black text-white text-xs px-2 py-1 rounded
                                       opacity-0 group-hover:opacity-100 transition
                                       whitespace-nowrap z-50"
                          >
                            {vote.user?.name}
                          </div>
                        </div>
                      )
                  )}
                </div>
              </div>

              {/* Ligne 2 : Voter + icône */}
              <div className="flex items-center gap-2">
                <span className="font-bold text-sm">Voter :</span>
                <button
                  onClick={handleVote}
                  disabled={voteLoading}
                  onMouseEnter={() => setHoverVote(true)}
                  onMouseLeave={() => setHoverVote(false)}
                  className="w-10 h-10 transition-transform hover:scale-110"
                >
                  <img
                    src={
                      hasVoted
                        ? hoverVote
                          ? "/images/voteAvant.png"
                          : "/images/voteApres.png"
                        : hoverVote
                        ? "/images/voteApres.png"
                        : "/images/voteAvant.png"
                    }
                    alt="Vote"
                    className="w-full h-full object-contain"
                  />
                </button>
              </div>
            </div>

            {/* Auteur */}
            <div className="flex items-center gap-3 mb-4">
              {auteurPreview && (
                <img
                  src={auteurPreview}
                  alt={projet.auteur?.name}
                  className="w-10 h-10 rounded-full"
                />
              )}
              <div>
                <div className="font-semibold">{projet.auteur?.name}</div>
                <div className="text-sm text-gray-500">Auteur</div>
              </div>
            </div>

            <h1 className="text-3xl font-bold mb-4">{projet.nom}</h1>
            <p className="text-gray-700 mb-6">{projet.descriptionCourte}</p>

            <div className="grid grid-cols-[80px_1fr] gap-y-2 mb-6">
              <div className="font-bold">Période :</div>
              <div>
                {formatDate(projet.dateDebut)} au {formatDate(projet.dateFin)}
              </div>

              <div className="font-bold">Coût :</div>
              <div>{projet.cout.toLocaleString()} $</div>
            </div>

            <div
              className="prose max-w-none"
              dangerouslySetInnerHTML={{ __html: projet.html }}
            />
          </div>
        </div>

        {/* Flèche droite */}
        {projetSuivant && (
          <button
            onClick={() => navigate(`/admin/projets/${projetSuivant._id}`)}
            className="mt-[25%] w-14 h-14 rounded-full bg-blue-600 text-white text-4xl"
          >
            ›
          </button>
        )}
      </div>
    </div>
  );
};

export default PageProjet;
